import axios from 'axios';
import type { Draw, Lottery } from '@/types/Lottery';
import { baseUrl } from '../base';

export type LotteryWritePayload = Pick<
  Lottery,
  | 'name'
  | 'description'
  | 'startDate'
  | 'endDate'
  | 'maxParticipants'
  | 'totalWinners'
  | 'emailTemplate'
  | 'smsTemplate'
  | 'notSelectedEmailTemplate'
  | 'notSelectedSmsTemplate'
  | 'termsAndConditions'
  | 'confirmationMessage'
  | 'isActive'
  | 'inActiveValidationErrorMessage'
>;

const LOTTERY_WRITE_FIELDS = [
  'name',
  'description',
  'startDate',
  'endDate',
  'maxParticipants',
  'totalWinners',
  'emailTemplate',
  'smsTemplate',
  'notSelectedEmailTemplate',
  'notSelectedSmsTemplate',
  'termsAndConditions',
  'confirmationMessage',
  'isActive',
  'inActiveValidationErrorMessage',
] as const satisfies readonly (keyof LotteryWritePayload)[];

function toLotteryWritePayload(data: Partial<Lottery>): Partial<LotteryWritePayload> {
  const payload = Object.fromEntries(
    LOTTERY_WRITE_FIELDS.filter((key) => data[key] !== undefined).map((key) => [key, data[key]])
  ) as Partial<LotteryWritePayload>;

  if (payload.maxParticipants !== undefined) {
    payload.maxParticipants = Number(payload.maxParticipants);
  }

  if (payload.totalWinners !== undefined) {
    payload.totalWinners = Number(payload.totalWinners);
  }

  return payload;
}

export const getLotteries = async (): Promise<any> => {
  return axios.get(`${baseUrl.SHERPA}/lotteries`);
};

export const createLottery = async (lotteryData: Partial<Lottery>): Promise<{ data: Lottery }> => {
  return axios.post(`${baseUrl.SHERPA}/lotteries`, toLotteryWritePayload(lotteryData));
};

export const getLotteryDetails = async (id: string): Promise<any> => {
  return axios.get(`${baseUrl.SHERPA}/lotteries/${id}`);
};

export const updateLottery = async (id: string, data: Partial<Lottery>): Promise<{ data: Lottery }> => {
  return axios.patch(`${baseUrl.SHERPA}/lotteries/${id}`, toLotteryWritePayload(data));
};

export const deleteLottery = async (id: string): Promise<void> => {
  await axios.delete(`${baseUrl.SHERPA}/lotteries/${id}`);
};

export const createDraw = async (drawData: { name: string; lotteryId: string; totalWinners: number }): Promise<{ data: Draw }> => {
  // Ensure totalWinners is a number
  const dataToSend = {
    ...drawData,
    totalWinners: Number(drawData.totalWinners),
  };
  return axios.post(`${baseUrl.SHERPA}/lotterie-draw`, dataToSend);
};

export const submitNomineeApplication = async (
  lotteryId: string,
  nomineeData: {
    consent: boolean;
    name: string;
    email: string;
    contactInfo: string;
    address: string;
    additionalInfo: {
      secondaryInfo: {
        email: string;
        contactInfo: string;
      };
    };
  }
): Promise<any> => {
  return axios.post(`${baseUrl.TERMINAL}/lotteries/${lotteryId}/nominee-applications`, nomineeData);
};

export const getNomineeApplications = async (lotteryId: string, params?: Record<string, any>): Promise<any> => {
  return axios.get(`${baseUrl.TERMINAL}/lotteries/${lotteryId}/nominee-applications`, { params });
};

export const getDraws = async (lotteryId?: string): Promise<any> => {
  return axios.get(`${baseUrl.SHERPA}/lotterie-draw`, { params: { lotteryId } });
};

export const getDrawDetails = async (drawId: string): Promise<any> => {
  return axios.get(`${baseUrl.SHERPA}/lotterie-draw/${drawId}`);
};

export const executeDraw = async (drawId: string): Promise<any> => {
  return axios.post(`${baseUrl.SHERPA}/lotterie-draw/${drawId}/execute`);
};

export const updateDraw = async (drawId: string, data: Partial<Draw>): Promise<any> => {
  return axios.patch(`${baseUrl.SHERPA}/lotterie-draw/${drawId}`, data);
};

export const getDrawWinners = async (drawId: string): Promise<any> => {
  return axios.get(`${baseUrl.SHERPA}/lotterie-draw/${drawId}/winners`);
};

export const getLotteryDetailsPublic = async (id: string): Promise<any> => {
  return axios.get(`${baseUrl.SHERPA}/lotteries/${id}/public`);
};

export const excludeNominee = async (lotteryId: string, nomineeId: string, data: { remarks: string }): Promise<any> => {
  return axios.patch(`${baseUrl.TERMINAL}/lotteries/${lotteryId}/nominee-applications/${nomineeId}/exclude`, data);
};

export const sendNotification = async (lotteryId: string, data: { drawIds: number[] }): Promise<any> => {
  return axios.post(`${baseUrl.TERMINAL}/lotteries/${lotteryId}/nominee-applications/send-notification`, data);
};
